import React, { FC, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
  Box,
  CircularProgress,
  Link,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import Form, { FormDataModel } from '../../../components/Form';
import InputField from '../../../components/InputField';
import axiosInstance from '../../../utils/axios';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import { removeErrorFieldsFromValues, Validators } from '../../../utils/validators';
import AuthPageShell, { AuthPageMessage, authLinkSx, authPrimaryButtonSx } from '../AuthPageShell';
import { ResetForm } from './ResetForm';

const Reset: FC = () => {
  const Navigate = useCustomNavigate();
  const params = useLocation().search;
  const email = new URLSearchParams(params).get('email');
  const accessToken = new URLSearchParams(params).get('access-token');
  const refresh = new URLSearchParams(params).get('refresh-token');
  const firstTimeLogin = new URLSearchParams(params).get('firstTimeLogin');
  const orgId = new URLSearchParams(params).get('orgId');

  const theme = useTheme();
  const resetForm: RefObject<Form | null | undefined> = useRef();
  const [hasError, setHasError] = useState(false);
  const [hasValidCode, setHasValidCode] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [message, setMessage] = useState<AuthPageMessage>({
    display: false,
    severity: null,
    message: '',
  });

  const steps = ['Verify OTP', 'Reset Password'];

  useEffect(() => {
    if (firstTimeLogin && email && accessToken && refresh) {
      const query = new URLSearchParams({
        email,
        'access-token': accessToken,
        'refresh-token': refresh,
        ...(orgId ? { orgId } : {}),
      }).toString();
      Navigate(`${routes.firstTimeChangePassword}?${query}`, { replace: true });
    }
  }, [firstTimeLogin, email, accessToken, refresh, orgId, Navigate]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const id = setInterval(() => {
      setResendCountdown(s => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [resendCountdown]);

  const handleReset = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = resetForm.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const body = removeErrorFieldsFromValues(formData);
    try {
      setHasError(false);
      if (isFormValid) {
        setLoading(true);
        setCode('');
        const response = await axiosInstance.post(URLS.reset, {
          email,
          code,
          mailType: 'FORGOT_PASSWORD',
          password: body.password,
          confirmPassword: body.confirmPassword,
        });
        if (response.status === 200) {
          setMessage({
            display: true,
            message: strings.password_reset_success_message,
            severity: 'success',
          });
          setTimeout(() => Navigate(routes.login), 500);
        }
      } else {
        setHasError(true);
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error.response?.data?.error_description ??
          error.response?.data?.message ??
          error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCode = async () => {
    try {
      if (code) {
        setOtpLoading(true);
        const { status } = await axiosInstance.post(URLS.verifyCode, {
          identifier: email,
          mailType: 'FORGOT_PASSWORD',
          code,
        });
        if (status === 200) {
          setMessage({
            display: true,
            message: strings.code_validated_success_message,
            severity: 'success',
          });
          setHasValidCode(true);
          setActiveStep(1);
        }
      }
    } catch (e: any) {
      setMessage({
        display: true,
        message: `${e.response?.data?.message || strings.tryAgainMessage}`,
        severity: 'error',
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0 || resendLoading) return;
    try {
      setResendLoading(true);
      const { status } = await axiosInstance.post(URLS.resend, {
        identifier: email,
        mailType: 'FORGOT_PASSWORD',
      });
      if (status === 200) {
        setMessage({
          display: true,
          message: 'Verification code resent successfully.',
          severity: 'success',
        });
        setResendCountdown(60);
      }
    } catch (e: any) {
      setMessage({
        display: true,
        message: e?.response?.data?.message || strings.tryAgainMessage,
        severity: 'error',
      });
    } finally {
      setResendLoading(false);
    }
  };

  if (firstTimeLogin && email && accessToken && refresh) {
    return null;
  }

  return (
    <AuthPageShell
      title={!hasValidCode ? 'Reset Password' : 'Set New Password'}
      subtitle={
        !hasValidCode
          ? `We've sent a verification code to ${email || 'your email'}.`
          : 'Choose a new password for your account.'
      }
      message={message}
      onCloseNotification={value => setMessage(prev => ({ ...prev, display: value }))}>
      <Box mb={3}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {!hasValidCode && (
        <Box>
          <InputField
            data-testid="otp-input"
            autoFocus
            inputProps={{ maxLength: 6 }}
            inputValue={code}
            textChange={setCode}
            label="Enter 6-digit code"
            validators={[
              {
                check: Validators.validateMandatoryField,
                message: strings.requiredText,
              },
            ]}
          />
          <PrimaryButton
            data-testid="verify-otp-btn"
            disabled={otpLoading || code.trim() === ''}
            sx={authPrimaryButtonSx}
            fullWidth
            onClick={handleCode}>
            {otpLoading && <CircularProgress sx={{ mr: 1 }} size={20} />}
            Verify Code
          </PrimaryButton>

          <Box mt={2} display="flex" justifyContent="center" flexWrap="wrap" gap={0.5}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Didn&apos;t receive the code?
            </Typography>
            <Typography
              data-testid="resend-otp-btn"
              onClick={handleResendCode}
              sx={{
                color: resendCountdown > 0 ? 'text.secondary' : theme.palette.primary.main,
                cursor: resendCountdown > 0 ? 'not-allowed' : 'pointer',
                textDecoration: resendCountdown > 0 ? 'none' : 'underline',
              }}>
              {resendLoading
                ? 'Sending...'
                : resendCountdown > 0
                  ? `Resend in ${resendCountdown}s`
                  : 'Resend Code'}
            </Typography>
          </Box>
        </Box>
      )}

      {hasValidCode && (
        <Box>
          <Typography variant="subtitle2" marginY={2} textAlign="center" color="success.main">
            Code verified successfully. Set your new password below.
          </Typography>
          <Form
            hasError={hasError}
            ref={resetForm as RefObject<Form>}
            model={ResetForm(strings, firstTimeLogin as any)}
            values={{}}
          />
          <PrimaryButton
            data-testid="reset-password-btn"
            disabled={loading}
            sx={authPrimaryButtonSx}
            fullWidth
            onClick={handleReset}>
            {loading && <CircularProgress sx={{ mr: 1 }} size={20} />}
            Reset Password
          </PrimaryButton>
        </Box>
      )}

      <Link underline="none">
        <Typography
          variant="body2"
          sx={authLinkSx(theme.palette.primary.main)}
          onClick={() => Navigate(routes.login)}>
          Back to Login
        </Typography>
      </Link>
    </AuthPageShell>
  );
};

export default Reset;
